import { FC, useState } from 'react';
import { FaBars } from 'react-icons/fa6';
import { Link, useLocation } from 'react-router-dom';

import { Box, Container, Divider, Drawer, Grid2, IconButton, List, ListItemText, Typography } from '@mui/material';

import { useTheme } from 'themes/themeProvider';

import { useSelector } from 'react-redux';
import { useFeatures } from 'hooks/useFeatures';
import { AddMinerModal } from 'shared/HostedMinersCard/AddMinerModal';
import { UserInformationMissingModal } from 'shared/Modals';
import { productsSelector } from 'store/slices/products-slice';
import { userDataSelector } from 'store/slices/user-slice';
import { Logo } from './../Logo/Logo';
import { UserMenu } from './UserMenu/UserMenu';
// import { NotificationMenu } from './NotificationsMenu/NotificationMenu';

export const Header: FC = () => {
  const { theme } = useTheme();
  const { pathname } = useLocation();
  const products = useSelector(productsSelector);
  const user = useSelector(userDataSelector);
  // Plan 06: "Miners" nav is mining-guarded (default-visible); "Servers" nav is colo opt-in.
  const { miningEnabled, coloEnabled } = useFeatures();

  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [showMissingInfoModal, setShowMissingInfoModal] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);

  const onToggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeComingSoonModal = () => {
    setShowComingSoonModal(false);
  };

  const closeMissingInfoModal = () => {
    setShowMissingInfoModal(false);
  };

  return (
    <>
      <Box sx={{ backgroundColor: theme.headerColor }} py={4}>
        <Container fixed maxWidth='xl'>
          <Grid2 container alignItems='center' justifyContent='space-between'>
            {/* Burger Menu Icon */}
            <Grid2 sx={{ display: { xs: 'block', md: 'none' } }}>
              <IconButton onClick={onToggleMenu}>
                <FaBars size={32} color={theme.headerTextColor} />
              </IconButton>
            </Grid2>
            {/* Logo Section */}
            <Grid2
              sx={{
                justifyContent: 'center',
                display: { xs: 'none', md: 'block' },
              }}
            >
              <Link to='/'>
                <Logo />
              </Link>
            </Grid2>

            {/* Navigation Links */}
            <Grid2 sx={{ display: { flex: 1, xs: 'none', md: 'block' } }}>
              <Grid2 container spacing={4} sx={{ ml: 4 }}>
                <Grid2>
                  <Link
                    to='/'
                    style={{ color: theme.headerTextColor, textDecoration: pathname === '/' ? 'underline' : 'none' }}
                  >
                    <Typography variant='body1'>Dashboard</Typography>
                  </Link>
                </Grid2>
                {miningEnabled && (
                  <Grid2>
                    <Link
                      to='/hosted-miners'
                      style={{
                        color: theme.headerTextColor,
                        textDecoration: pathname === '/hosted-miners' ? 'underline' : 'none',
                      }}
                    >
                      <Typography variant='body1'>My Miners</Typography>
                    </Link>
                  </Grid2>
                )}
                {miningEnabled && (
                  <Grid2>
                    <Link
                      to='/my-wallets'
                      style={{
                        color: theme.headerTextColor,
                        textDecoration: pathname.includes('my-wallets') ? 'underline' : 'none',
                      }}
                    >
                      <Typography variant='body1'>My Wallets</Typography>
                    </Link>
                  </Grid2>
                )}
                {miningEnabled && (
                  <Grid2>
                    <Link
                      to={products.length > 0 ? '/shop' : '#'}
                      onClick={e => {
                        if (products.length === 0) {
                          e.preventDefault();
                          setShowComingSoonModal(true);
                        } else if (!user?.isFullInfoProvided) {
                          e.preventDefault();
                          setShowMissingInfoModal(true);
                        }
                      }}
                      style={{
                        color: theme.headerTextColor,
                        textDecoration: pathname.includes('shop') ? 'underline' : 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant='body1'>Shop</Typography>
                    </Link>
                  </Grid2>
                )}
                {coloEnabled && (
                  <Grid2>
                    <Link
                      to='/servers'
                      style={{
                        color: theme.headerTextColor,
                        textDecoration: pathname === '/servers' ? 'underline' : 'none',
                      }}
                    >
                      <Typography variant='body1'>Servers</Typography>
                    </Link>
                  </Grid2>
                )}
                <Grid2>
                  <Link
                    to='/tasks'
                    style={{
                      color: theme.headerTextColor,
                      textDecoration: pathname === '/tasks' ? 'underline' : 'none',
                    }}
                  >
                    <Typography variant='body1'>Tasks</Typography>
                  </Link>
                </Grid2>
              </Grid2>
            </Grid2>

            {/* Right Section */}
            <Grid2>
              <Grid2 container alignItems='center' spacing={4}>
                {/* <Grid2>
                <NotificationMenu />
              </Grid2> */}

                <Grid2>
                  <Typography
                    component='a'
                    href={theme?.contactSupportLink || '#'}
                    target={theme?.contactSupportLink?.startsWith('mailto:') ? undefined : '_blank'}
                    rel={theme?.contactSupportLink?.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                    sx={{ color: theme.headerTextColor, cursor: 'pointer' }}
                  >
                    Support
                  </Typography>
                </Grid2>

                <Grid2>
                  <UserMenu />
                </Grid2>
              </Grid2>
            </Grid2>
          </Grid2>

          {/* Drawer for Mobile Menu */}
          <Drawer anchor='left' open={menuOpen} onClose={onToggleMenu}>
            <Box sx={{ backgroundColor: theme.headerColor }} height={'100%'}>
              <Box p={2} py={3}>
                <Link to='/'>
                  <Logo />
                </Link>
              </Box>
              <List>
                <ListItemText
                  primary={
                    <Link to='/' onClick={onToggleMenu}>
                      <Box sx={{ width: '100%', display: 'block' }}>Dashboard</Box>
                    </Link>
                  }
                  sx={{ color: theme.headerTextColor, px: 2, py: 1 }}
                />

                {miningEnabled && (
                  <>
                    <Divider sx={{ mx: 2 }} color={theme.headerTextColor} />

                    <ListItemText
                      primary={
                        <Link to='/hosted-miners' onClick={onToggleMenu}>
                          <Box sx={{ width: '100%', display: 'block' }}>My Miners</Box>
                        </Link>
                      }
                      sx={{ color: theme.headerTextColor, px: 2, py: 1 }}
                    />
                  </>
                )}

                {miningEnabled && (
                  <>
                    <Divider sx={{ mx: 2 }} color={theme.headerTextColor} />

                    <ListItemText
                      primary={
                        <Link to='/my-wallets' onClick={onToggleMenu}>
                          <Box sx={{ width: '100%', display: 'block' }}>My Wallets</Box>
                        </Link>
                      }
                      sx={{ color: theme.headerTextColor, px: 2, py: 1 }}
                    />
                  </>
                )}

                {coloEnabled && (
                  <>
                    <Divider sx={{ mx: 2 }} color={theme.headerTextColor} />

                    <ListItemText
                      primary={
                        <Link to='/servers' onClick={onToggleMenu}>
                          <Box sx={{ width: '100%', display: 'block' }}>Servers</Box>
                        </Link>
                      }
                      sx={{ color: theme.headerTextColor, px: 2, py: 1 }}
                    />
                  </>
                )}

                <Divider sx={{ mx: 2 }} color={theme.headerTextColor} />

                <ListItemText
                  primary={
                    <Link to='/tasks' onClick={onToggleMenu}>
                      <Box sx={{ width: '100%', display: 'block' }}>Tasks</Box>
                    </Link>
                  }
                  sx={{ color: theme.headerTextColor, px: 2, py: 1 }}
                />
              </List>
            </Box>
          </Drawer>
        </Container>
      </Box>
      {showComingSoonModal && <AddMinerModal onClose={closeComingSoonModal} />}
      {showMissingInfoModal && <UserInformationMissingModal isModalOpen onClose={closeMissingInfoModal} />}
    </>
  );
};
